import React, { useState, useEffect, useCallback, memo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Audio from '../../atoms/feedback/audio'

import messages from '../../../services/messages'
import { convertSlaTime, getTimestamp } from '../../../utils/customMethods'
import {
  handleSLA,
  shouldUpdateIdleMsg,
  getTriggers,
} from '../../../utils/messages'

import { useTheme } from '@material-ui/core/styles'

import { Duration } from 'luxon'

import { setActiveConversation } from '../../../redux/activeConversation/actions'
import { setConversations } from '../../../redux/conversations/actions'

import {
  CheckCircleRounded as Status1Icon,
  ErrorRounded as Status2Icon,
  Brightness2Rounded as Status3Icon,
} from '@material-ui/icons'

import Typography from '../../atoms/display/typography'

const Status = (props) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const isAgentMsg = (msg) => msg?.by === 'agent' ?? false

  const activeConversation = useSelector((state) => state.activeConversation)
  const conversation = useSelector(
    (state) => state.conversations.conversations[props?.chatId]
  )
  const messages = useSelector((state) => state.conversations.messages)

  const [status, setStatus] = useState(null)
  const [timer, setTimer] = useState(undefined)
  const [reqCounter, setReqCounter] = useState(0)

  const handleTimer = useCallback(
    (triggers, idleMsgTime, reset) => {
      const getTimer = (prev) => {
        if (reset) {
          const msgTime = new Date(idleMsgTime).getTime()
          const now = Date.now()
          const sla = parseInt(triggers?.sla)
          const delta = Math.ceil(msgTime / 1000 + sla - now / 1000)

          return delta
        } else {
          return prev - 1
        }
      }

      setTimer(getTimer)
      setStatus((prev) => {
        if (prev !== 'inactive' || reset) {
          const sla = handleSLA(triggers)
          if (sla === 'inactive') updateStatus()
          return sla
        } else {
          prev
        }
      })
    },
    [getTimestamp, setTimer]
  )

  const updateStatus = useCallback(() => {
    if (conversation?.status !== 'inactive') {
      // Update status
      conversation.status = 'inactive'

      // If this conversation is opened in active area, remove it
      // if (activeConversation === conversation?.conversationId)
      // dispatch(setActiveConversation(null))

      // update attempts
      setReqCounter(1)

      // update store
      dispatch(setConversations(conversation))
    }
  }, [
    conversation,
    activeConversation,
    setActiveConversation,
    setConversations,
    setReqCounter,
    dispatch,
  ])

  const handleStatus = useCallback(
    (triggers, lastMessage, reset) => {
      // Set status if timer should be visible OR previous status !== inactive
      setStatus((prev) => {
        if (prev !== 'inactive' || reset) {
          const sla = handleSLA(triggers)
          if (sla === 'inactive') updateStatus()
          if(sla ==='overdue' && prev !== 'overdue' && prev) {
            playAudioOverdue()
          }
          return sla
        } else {
          prev
        }
      })

      // set timer if it should be visible (sender msg !== agent)
      if (lastMessage?.by === 'agent')
        handleTimer(triggers, lastMessage.sentTime, reset)
    },
    [handleTimer, updateStatus]
  )

  const updateComponent = useCallback(() => {
    // Update status triggers
    if (isAgentMsg(conversation?.lastMessage)) {
      conversation.statusTriggers = getTriggers(conversation)

      const chatStatus = conversation?.status
      const triggers = conversation?.statusTriggers
      const idleMsgTime = conversation?.idleMessage?.sentTime

      if (triggers && chatStatus && idleMsgTime) {
        if (
          chatStatus !== 'inactive' &&
          chatStatus !== 'closed' &&
          chatStatus !== 'pos-chat' &&
          conversation?.lastMessage?.by === 'agent'
        ) {
          const lastMessage = conversation?.lastMessage

          handleStatus(triggers, lastMessage, true)
          const interval = setInterval(
            () => handleStatus(triggers, lastMessage, false),
            1000
          )
          return () => clearInterval(interval)
        } else {
          setStatus(chatStatus)
        }
      } else if (
        !conversation?.status &&
        !conversation?.statusTriggers &&
        !conversation?.idleMessage?.sentTime
      ) {
        setStatus('closed')
      }
    }
  }, [conversation, handleStatus, setStatus])

  const handleConsumerIdle = useCallback(async () => {
    const chatId = conversation?.conversationId
    if (chatId) {
      try {
        const response = await messages.post(
          `/tech4h-messages/conversation/change-status?id=${chatId}`,
          { status: 'inactive' }
        )

        if (response.status !== 200) throw response.data
      } catch (error) {
        setTimeout(() => setReqCounter((prev) => prev + 1), 1500)
      }
    }
  }, [conversation?.conversationId, setReqCounter])

  const formatter = useCallback((sec) => {
    if (props?.ticket) {
      return convertSlaTime(sec, {
        withSummary: props?.withSummary,
      })?.summary
    }

    if (sec !== undefined && sec !== null) {
      const seconds = Math.abs(sec)
      const prefix = sec >= 0 ? '' : '-'
      const sufixH = seconds < 7200 ? 'hora' : 'horas'
      return (
        prefix +
        Duration.fromObject({
          seconds: seconds,
        }).toFormat(
          seconds < 60 ? '00:ss' : seconds < 3600 ? 'mm:ss' : 'hh:mm:ss'
        )
      )
    }
  }, [])

  const renderIcon = useCallback(() => {
    if (status === 'closed' || status === 'pos-chat') {
      // TODO: add closed icon in component
    } else if (status === 'inactive') {
      return (
        <>
          <Status3Icon
            id="Inactive"
            style={{ fontSize: 18, color: theme.palette.text.tertiary }}
          />
        </>
      )
    } else if (status === 'overdue') {
      return (
        <>
          <Status2Icon
            style={{ fontSize: 18, color: theme.palette.status[3] }}
          />
          <Typography
            variant="body2"
            style={{
              fontSize: theme.typography.fontSize - 3,
              color: theme.palette.status[3],
              marginTop: 5,
            }}
          >
            {formatter(timer)}
          </Typography>
        </>
      )
    } else if (status === 'bursting') {
      return (
        <>
          <Status2Icon
            style={{ fontSize: 18, color: theme.palette.status[2] }}
          />
          <Typography
            variant="body2"
            style={{
              fontSize: theme.typography.fontSize - 3,
              color: theme.palette.status[2],
              marginTop: 5,
            }}
          >
            {formatter(timer)}
          </Typography>
        </>
      )
    } else if (
      status === 'normal' ||
      status === 'bursting' ||
      status === 'active'
    ) {
      return (
        <>
          <Status1Icon
            style={{ fontSize: 18, color: theme.palette.status[0] }}
          />
          {!props?.triggers?.inactive && (
            <Typography
              variant="body2"
              style={{
                fontSize: theme.typography.fontSize - 3,
                color: theme.palette.status[0],
                marginTop: 5,
              }}
            >
              {formatter(timer)}
            </Typography>
          )}
        </>
      )
    }
    return null
  }, [status, timer])

  useEffect(() => {

    if (!conversation) return void 0

    if (!conversation?.lastMessage) return void 0
  
    if (isAgentMsg(conversation?.lastMessage)) {
      //in case the agent send message
      conversation.statusTriggers = getTriggers(conversation)
      if (
        conversation.status &&
        conversation.statusTriggers &&
        conversation.idleMessage?.sentTime
      ) {
        if (!['inactive', 'closed', 'pos-chat'].includes(conversation.status)) {
          handleStatus(
            conversation.statusTriggers,
            conversation.lastMessage,
            true
          )

          const interval = setInterval(
            () =>
              handleStatus(
                conversation.statusTriggers,
                conversation.lastMessage,
                false
              ),
            1000
          )

          return () => clearInterval(interval)
        } else return setStatus(conversation.status)
      } else return setStatus('closed')
    }
    else {
      //in case the agent has already sent a message previously and the page may have had a reload
      let msg = messages[conversation.conversationId];
      let lastAgentMessage = null;
      lastAgentMessage = msg.slice().reverse().filter(message => message.by === 'agent')[0] || null;
      if (lastAgentMessage) {
        conversation.lastMessage = lastAgentMessage;
        conversation.statusTriggers = getTriggers(conversation)
        handleStatus(
          conversation.statusTriggers,
          lastAgentMessage,
          true
        )

        const interval = setInterval(
          () =>
            handleStatus(
              conversation.statusTriggers,
              lastAgentMessage,
              false
            ),
          1000
        )
    
        return () => clearInterval(interval)
      }else{
        //in case the agent has not sent a message previously and the page may have had a reload
        let lastAgentMessage = {
          "sentTime": conversation.assignedTime,
          "receivedTime": null,
          "by": "agent",
          "private": false,
          "location": null
      }
        conversation.lastMessage = lastAgentMessage;
        conversation.statusTriggers = getTriggers(conversation)
        handleStatus(
          conversation.statusTriggers,
          lastAgentMessage,
          true
        )

        const interval = setInterval(
          () =>
            handleStatus(
              conversation.statusTriggers,
              lastAgentMessage,
              false
            ),
          1000
        )
    
        return () => clearInterval(interval)
      }
    }
  }, [conversation])

  useEffect(() => {
    reqCounter > 0 && reqCounter <= 3 ? handleConsumerIdle() : setReqCounter(0)
  }, [reqCounter, setReqCounter])

const playAudioOverdue = () => {
  try {
    Audio.play('overdueConversation')
  } catch (error) {
    console.log('Error on play audio', error)
  }
};

  return renderIcon()
}

export default memo(Status)
